import { FunctionComponent, useContext, useState } from "react";
import { Image, GrabCADApplicationColors } from "grabcad-ui-elements";
import { IScreenProps } from "../../screens/Screen";
import UsersSvg from "../../images/home/users.svg";
import PrintersSvg from "../../images/home/printers.svg";
import LicensesSvg from "../../images/home/licenses.svg";
import HomePageSvg from "../../images/home/network.svg";
import PlayBtnSvg from "../../images/play_btn.svg";
import UserGroupsSvg from "../../images/thumb_user_groups.svg";
import UsersSvgOld from "../../images/thumb_users.svg";
import LicensesSvgOld from "../../images/thumb_licenses.svg";
import { PATHS } from "../../Routes";
import { ApplicationContext } from "../../components/ApplicationProvider";
import { UserContext } from "../../components/User/UserProvider";
import { Role, useCompanyAsAdminQuery, useUsersQuery } from "../../graphql";
import { useGetAllPrinters } from "../Printer/UseGetAllPrinters";
import { MappedPrinterStatus } from "../../utils/printer_utils";
import { useCompanyLicensesQuery } from "../../graphql/Queries/Licenses/GetCompanyLicenses";
import SsoBanner from "./Carousel/SsoBanner";
import OfflineLicensingBanner from "./Carousel/OfflineLicensingBanner";
import Carousel from "./Carousel/Carousel";
import AnalyzeBanner from "./Carousel/AnalyzeBanner";
import { HomePage, HomePageColor, HomePageLabel, HomePageOld } from "./HomePageStyle";

export const CompanyAdminHomePage: FunctionComponent<IScreenProps> = (props) => {
    const { t } = useContext(ApplicationContext);
    const { role } = useContext(UserContext);
    const isReadOnlyCompanyUser = role === Role.User;
    const { companyLoading, company } = useCompanyAsAdminQuery();
    const { companyLicenses, allEnabledFeatureKeys } = useCompanyLicensesQuery();
    const { users, loadingUsers } = useUsersQuery();
    const { loadingPrinters, printers, errorOccurred } = useGetAllPrinters();

    let idlePrinterCount = 0;
    let workPrinterCount = 0;
    let errPrinterCount = 0;
    let otherPrinterCount = 0;

    if (!(loadingPrinters || errorOccurred)) {
        printers.forEach((printer) => {
            const mappedStatus = new MappedPrinterStatus(printer);
            switch (mappedStatus.statusText) {
                case `Harvesting`:
                case `Printing`:
                    workPrinterCount++;
                    break;
                case `Idle`:
                    idlePrinterCount++;
                    break;
                case "Paused":
                case "Error":
                case "Maintenance":
                case "Warning":
                case "OutOfMaterial":
                case "PendingStart":
                case "Disconnected":
                case "Cancelled":
                case "Scheduled":
                case "Missing":
                    errPrinterCount++;
                    break;
                default:
                    otherPrinterCount++;
            }
        });
    }

    let activeLicenseCount = 0;
    let expiredLicenseCount = 0;
    let expiringLicenseCount = 0;
    companyLicenses?.licenses.forEach((license) => {
        if (license.state === "active") {
            activeLicenseCount++;
        } else if (license.state === "expired") {
            expiredLicenseCount++;
        } else if (license.state === "expiring") {
            expiringLicenseCount++;
        }
    });

    const [stopCarouselRefresh, setStopCarouselRefresh] = useState(false);

    const slides: JSX.Element[] = [];
    // eligibleForSSO also means user has at least a streamline pro license
    const eligibleForSSO = company?.eligibleForSSO ?? false;

    if (allEnabledFeatureKeys.has("analyze_saas_user") && company?.analyzeDeploymentType === null) {
        slides.push(
            <AnalyzeBanner
                key="1"
                onCTAButtonClick={() => setStopCarouselRefresh(true)}
                onCancel={() => setStopCarouselRefresh(false)}
                hasAnalyzeSAASFeature={true}
            />
        );
    }

    if (company?.authSettings?.sso == null) {
        slides.push(<SsoBanner eligibleForSSO={eligibleForSSO} key="1" />);
    }

    slides.push(<OfflineLicensingBanner key="2" />);

    return (
        <>
            <Carousel slides={slides} stopCarouselRefresh={stopCarouselRefresh} />
            <HomePage>
                <div>
                    <div className="card hero">
                        <img src={HomePageSvg} />
                        <div>
                            <h1>{t("homepage.header")}</h1>
                            <p>{t("homepage.company_admin_ctrl")}</p>
                        </div>
                    </div>
                    <div className="linkCards">
                        <div className="card" onClick={() => props.history.push(PATHS.users)}>
                            <Image src={UsersSvg} />
                            <h2>{t("homepage.users.title")}</h2>
                            <div className="labelContainer">
                                {loadingUsers ? (
                                    <i className="notched circle loading icon"></i>
                                ) : (
                                    <HomePageLabel color={HomePageColor.LABEL_NEUTRAL}>
                                        {t("homepage.users.count", { userCount: users.length })}
                                    </HomePageLabel>
                                )}
                                {companyLoading ? (
                                    <i className="notched circle loading icon"></i>
                                ) : (
                                    <HomePageLabel color={HomePageColor.LABEL_NEUTRAL}>
                                        {t("homepage.users.count_admins", {
                                            adminCount: company?.admins.length,
                                        })}
                                    </HomePageLabel>
                                )}
                            </div>
                        </div>
                        {!isReadOnlyCompanyUser && (
                            <div
                                id="qa-homepage-printers-card"
                                className="card"
                                onClick={() => props.history.push(PATHS.companyPrinters)}
                            >
                                <Image src={PrintersSvg} />
                                <h2>{t("homepage.printers.title")}</h2>
                                <div className="labelContainer">
                                    {errorOccurred ? (
                                        <HomePageLabel
                                            color={GrabCADApplicationColors.GC_ERROR_SEMANTIC}
                                        >
                                            {t("printers.home_error_text")}
                                        </HomePageLabel>
                                    ) : loadingPrinters ? (
                                        <i className="notched circle loading icon"></i>
                                    ) : (
                                        <HomePageLabel
                                            color={GrabCADApplicationColors.GC_SUCCESS_SEMANTIC}
                                        >
                                            {t("homepage.printers.count_printing", {
                                                printerCount: workPrinterCount,
                                            })}
                                        </HomePageLabel>
                                    )}
                                    {errorOccurred || loadingPrinters ? null : (
                                        <HomePageLabel color={HomePageColor.LABEL_NEUTRAL}>
                                            {t("homepage.printers.count_idle", {
                                                printerCount: idlePrinterCount,
                                            })}
                                        </HomePageLabel>
                                    )}
                                    {errorOccurred ||
                                    loadingPrinters ||
                                    otherPrinterCount < 1 ? null : (
                                        <HomePageLabel color={HomePageColor.LABEL_NEUTRAL}>
                                            {t("homepage.printers.count_other", {
                                                printerCount: otherPrinterCount,
                                            })}
                                        </HomePageLabel>
                                    )}
                                    {loadingPrinters || errPrinterCount < 1 ? null : (
                                        <HomePageLabel
                                            color={GrabCADApplicationColors.GC_WARNING_SEMANTIC}
                                        >
                                            {t("homepage.printers.count_error", {
                                                printerCount: errPrinterCount,
                                            })}
                                        </HomePageLabel>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="card" onClick={() => props.history.push(PATHS.licenses)}>
                            <Image src={LicensesSvg} />
                            <h2>{t("homepage.licenses.title")}</h2>
                            <div className="labelContainer">
                                {companyLoading ? (
                                    <i className="notched circle loading icon"></i>
                                ) : activeLicenseCount < 1 ? null : (
                                    <HomePageLabel
                                        color={GrabCADApplicationColors.GC_SUCCESS_SEMANTIC}
                                    >
                                        {t("homepage.licenses.count_active", {
                                            licenseCount: activeLicenseCount,
                                        })}
                                    </HomePageLabel>
                                )}
                                {expiringLicenseCount < 1 ? null : (
                                    <HomePageLabel
                                        color={GrabCADApplicationColors.GC_WARNING_SEMANTIC}
                                    >
                                        {t("homepage.licenses.count_expiring", {
                                            licenseCount: expiringLicenseCount,
                                        })}
                                    </HomePageLabel>
                                )}
                                {expiredLicenseCount < 1 ? null : (
                                    <HomePageLabel
                                        color={GrabCADApplicationColors.GC_ERROR_SEMANTIC}
                                    >
                                        {t("homepage.licenses.count_expired", {
                                            licenseCount: expiredLicenseCount,
                                        })}
                                    </HomePageLabel>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </HomePage>
        </>
    );
};

export const CompanyAdminHomePageOld: FunctionComponent<IScreenProps> = (props) => {
    const { t } = useContext(ApplicationContext);
    return (
        <HomePageOld>
            <div>
                <h1>{t("homepage.header")}</h1>
                <div className="card hero">
                    <a
                        href="https://grabcad-1.wistia.com/medias/au0rupg6fd"
                        target="_blank"
                        rel="noreferrer"
                    >
                        <Image src={PlayBtnSvg} />
                    </a>
                    <div>
                        <h2>GrabCAD Control</h2>
                        <p>{t("homepage.company_admin")}</p>
                        <p>{t("homepage.company_admin2")}</p>
                    </div>
                </div>
                <div className="linkCards">
                    <div className="card" onClick={() => props.history.push(PATHS.userGroups)}>
                        <h2>{t("homepage.user_groups.title")}</h2>
                        <div className="imgHolder">
                            <Image src={UserGroupsSvg} />
                        </div>
                        <p>{t("homepage.user_groups.copy")}</p>
                    </div>
                    <div className="card" onClick={() => props.history.push(PATHS.users)}>
                        <h2>{t("homepage.users.title")}</h2>
                        <div className="imgHolder">
                            <Image src={UsersSvgOld} />
                        </div>
                        <p>{t("homepage.users.copy")}</p>
                    </div>
                    <div className="card" onClick={() => props.history.push(PATHS.licenses)}>
                        <h2>{t("homepage.licenses.title")}</h2>
                        <div className="imgHolder">
                            <Image src={LicensesSvgOld} />
                        </div>
                        <p>{t("homepage.licenses.copy")}</p>
                    </div>
                </div>
            </div>
        </HomePageOld>
    );
};
